/**
 * After sign-in only: reads pending slips from local storage and POSTs in order (FIFO).
 * Guests never hit this path until authenticated.
 */

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { usePendingGuestMonkeyChallengeStore } from '../../shared/stores/pendingGuestMonkeyChallengeStore';
import { flushPendingGuestMonkeyChallenge } from '../helpers/guestMonkeyMirage.helper';
import {
  isPendingGuestMonkeyPayloadExpired,
  isPendingGuestMonkeyPayloadAbandoned,
} from '../helpers/guestMonkeyExpiry.helper';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { logger } from '../../helpers/logger.helper';

/**
 * HTTP status codes that indicate the server definitively rejected the payload.
 * These warrant discarding the slip (bad data). 401/429 keep it for retry.
 */
const DISCARD_HTTP_STATUSES = new Set([400, 403, 422]);

const RETRY_DELAY_MS = 30_000;

export function PendingGuestMonkeyChallengeFlush(): null {
  const { isSignedIn, isLoaded, userId, getToken } = useSupabaseAuth();
  const headLocalId = usePendingGuestMonkeyChallengeStore((s) => s.pendingQueue[0]?.localId);
  const dequeueAfterFlush = usePendingGuestMonkeyChallengeStore((s) => s.dequeueAfterFlush);
  const hydrated = usePendingGuestMonkeyChallengeStore((s) => s._hasHydrated);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { track } = useAnalytics();
  const flushingRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    return () => {
      if (retryTimerRef.current != null) clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !hydrated || !isSignedIn || !userId || !headLocalId) {
      return;
    }
    if (flushingRef.current) return;

    const snapshot = usePendingGuestMonkeyChallengeStore.getState().pendingQueue[0];
    if (!snapshot || snapshot.localId !== headLocalId) return;
    flushingRef.current = true;

    void (async () => {
      try {
        const token = await getToken();
        if (!token) {
          throw new Error('No session');
        }
        if (usePendingGuestMonkeyChallengeStore.getState().pendingQueue[0]?.localId !== snapshot.localId) {
          return;
        }
        if (isPendingGuestMonkeyPayloadAbandoned(snapshot)) {
          dequeueAfterFlush(snapshot.localId);
          track(ANALYTICS_EVENTS.GUEST_MONKEY_CHALLENGE_FLUSH_SKIPPED_POST_KICKOFF);
          showSnackbar(
            'That guest challenge was over 48 hours old and has been discarded. Create a new one anytime.',
            'info',
          );
          return;
        }
        if (isPendingGuestMonkeyPayloadExpired(snapshot)) {
          dequeueAfterFlush(snapshot.localId);
          track(ANALYTICS_EVENTS.GUEST_MONKEY_CHALLENGE_FLUSH_SKIPPED_POST_KICKOFF);
          showSnackbar(
            'All matches in that guest challenge had already ended, so we did not save it. Create a new one anytime.',
            'info',
          );
          return;
        }
        await flushPendingGuestMonkeyChallenge(snapshot, userId, token);
        if (usePendingGuestMonkeyChallengeStore.getState().pendingQueue[0]?.localId !== snapshot.localId) {
          return;
        }
        dequeueAfterFlush(snapshot.localId);
        await queryClient.invalidateQueries({ queryKey: ['challenges', 'active'] });
        await queryClient.invalidateQueries({ queryKey: ['friends', userId] });
        track(ANALYTICS_EVENTS.GUEST_MONKEY_CHALLENGE_FLUSHED);
        showSnackbar('Challenge locked in — you vs the Monkey!', 'success');
      } catch (e) {
        logger.error('[PendingGuestMonkeyChallengeFlush]', e);
        track(ANALYTICS_EVENTS.GUEST_MONKEY_CHALLENGE_FLUSH_FAILED);
        const httpStatus = axios.isAxiosError(e) && e.response != null ? e.response.status : null;
        if (httpStatus != null && DISCARD_HTTP_STATUSES.has(httpStatus)) {
          // Definitive bad-data rejection — discard slip.
          dequeueAfterFlush(snapshot.localId);
          showSnackbar(
            'Could not create your challenge. Open Create and try again, or contact support.',
            'error',
          );
        } else {
          // Transient error (network, 401, 429, 5xx) — keep slip and retry automatically.
          showSnackbar(
            'Could not reach the server. Your challenge is saved and will retry automatically.',
            'error',
          );
          retryTimerRef.current = setTimeout(() => setRetryKey((k) => k + 1), RETRY_DELAY_MS);
        }
      } finally {
        flushingRef.current = false;
      }
    })();
  }, [
    isLoaded,
    hydrated,
    isSignedIn,
    userId,
    headLocalId,
    retryKey,
    getToken,
    dequeueAfterFlush,
    queryClient,
    showSnackbar,
    track,
  ]);

  return null;
}
